import {
    Box,
    Button,
    Fade,
    IconButton,
    Modal,
    styled,
    TextFieldProps,
} from '@mui/material';
import { colors } from 'common/constants';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import UploadIcon from 'assets/images/icons/question-upload.svg';
import CloseIcon from '@mui/icons-material/Close';
type BaseImageUploadProps = TextFieldProps & {
    name: string;
};
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});
const BaseImageUpload: React.FC<BaseImageUploadProps> = ({
    name,
    ...other
}) => {
    const { control } = useFormContext();
    const [openModalPreview, setOpenModalPreview] = React.useState(false);
    const [image, setImage] = React.useState<File | string | null>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const handlePreview = (file: File | string) => {
        setImage(file);
        setOpenModalPreview(true);
    };

    return (
        <>
            <Controller
                name={name}
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <>
                        <Button
                            component="label"
                            role={undefined}
                            variant="contained"
                            tabIndex={-1}
                            sx={{
                                ...other.sx,
                                boxShadow: 'none',
                                padding: 0,
                                '&:hover': {
                                    backgroundColor: 'unset',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            <VisuallyHiddenInput
                                ref={inputRef}
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                    if (event?.target?.files?.[0]) {
                                        field.onChange(
                                            event?.target?.files?.[0]
                                        );
                                    }
                                    if (inputRef.current) {
                                        inputRef.current.value = '';
                                    }
                                }}
                            />

                            {!field?.value ? (
                                <Box
                                    sx={{
                                        maxWidth: '41px',
                                        height: '41px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <img src={UploadIcon} alt="img" />
                                </Box>
                            ) : (
                                <img
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handlePreview(field?.value);
                                    }}
                                    src={(() => {
                                        if (field?.value instanceof File) {
                                            return URL.createObjectURL(
                                                field?.value
                                            );
                                        } else if (
                                            typeof field?.value === 'string'
                                        ) {
                                            return field?.value;
                                        }
                                    })()}
                                    alt="img"
                                    style={{
                                        maxWidth: '41px',
                                        maxHeight: '41px',
                                    }}
                                />
                            )}
                        </Button>
                        {error && (
                            <Box
                                sx={{
                                    fontSize: '14px',
                                    marginLeft: 0,
                                    color: `${colors.textError} !important`,
                                    fontWeight: '400',
                                }}
                            >
                                {error?.message}
                            </Box>
                        )}
                    </>
                )}
            />
            <>
                <Modal
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                            backgroundcolor: 'red',
                        },
                    }}
                    open={openModalPreview}
                    onClose={() => {
                        setImage(null);
                        setOpenModalPreview(false);
                    }}
                    closeAfterTransition
                >
                    <Fade in={openModalPreview} timeout={100}>
                        <div
                            style={{
                                position: 'relative',
                                maxWidth: '90%',
                                maxHeight: '90%',
                            }}
                        >
                            <IconButton
                                onClick={() => {
                                    setImage(null);
                                    setOpenModalPreview(false);
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    color: 'white',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                            <img
                                src={
                                    image instanceof File
                                        ? URL.createObjectURL(image)
                                        : image ?? ''
                                }
                                alt="img-preview"
                                style={{
                                    maxHeight: '90vh',
                                    maxWidth: '90vw',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                }}
                            />
                        </div>
                    </Fade>
                </Modal>
            </>
        </>
    );
};

export default BaseImageUpload;
